import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getInitialLocale,
  getLocaleDefinition,
  isSupportedLocale,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
} from './locales';
import { translations } from './translations';

const I18nContext = createContext(null);

const interpolate = (message, variables) =>
  String(message).replace(/\{\{\s*([^{}\s]+)\s*\}\}/g, (match, name) => {
    if (!Object.prototype.hasOwnProperty.call(variables, name)) return match;
    return variables[name] == null ? '' : String(variables[name]);
  });

export const translate = (locale, key, variables = {}) => {
  const localizedValue = translations[locale]?.[key];
  const fallbackValue = translations.en[key];
  const value = localizedValue ?? fallbackValue;

  if (value == null) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`Missing English translation: ${key}`);
    }
    return variables.defaultValue ?? key;
  }

  if (typeof value === 'object') {
    const pluralKey = Number(variables.count) === 1 ? 'one' : 'other';
    return interpolate(value[pluralKey] ?? value.other ?? value.one ?? '', variables);
  }
  return interpolate(value, variables);
};

export const I18nProvider = ({ children, initialLocale }) => {
  const [locale, setLocaleState] = useState(() =>
    isSupportedLocale(initialLocale) ? initialLocale : getInitialLocale()
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((nextLocale) => {
    if (!isSupportedLocale(nextLocale)) return false;

    setLocaleState(nextLocale);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.setItem(LOCALE_STORAGE_KEY, nextLocale);
      }
    } catch {
      // The active language still changes when storage is unavailable.
    }
    return true;
  }, []);

  const t = useCallback((key, variables) => translate(locale, key, variables), [locale]);
  const formatNumber = useCallback(
    (value, options) => new Intl.NumberFormat(locale, options).format(value),
    [locale]
  );
  const formatDate = useCallback(
    (value, options) => {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? String(value ?? '')
        : new Intl.DateTimeFormat(locale, options).format(date);
    },
    [locale]
  );
  const formatRegion = useCallback((regionCode) => {
    try {
      return new Intl.DisplayNames([locale], { type: 'region' }).of(regionCode) || regionCode;
    } catch {
      return regionCode;
    }
  }, [locale]);

  const contextValue = useMemo(
    () => ({
      locale,
      localeDefinition: getLocaleDefinition(locale),
      locales: SUPPORTED_LOCALES,
      setLocale,
      t,
      formatNumber,
      formatDate,
      formatRegion,
    }),
    [formatDate, formatNumber, formatRegion, locale, setLocale, t]
  );

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
};
